// Reconnect/status polling resumes durable work; it never submits another scan.
export interface ScanHandle {
  jobId: string;
  sessionKey: string;
}
export interface ClientScanEvent {
  type: string;
  message?: string;
  report_url?: string;
  partial?: boolean;
  counts?: { total?: number };
  [key: string]: unknown;
}
export function readScanHandle(key: string): ScanHandle | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    return value &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.jobId) &&
      /^[A-Za-z0-9_-]{22}$/.test(value.sessionKey)
      ? value
      : null;
  } catch {
    return null;
  }
}
export function saveScanHandle(key: string, value: ScanHandle | null): void {
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch {
    /* Storage may be disabled; current-page reconnect still works. */
  }
}
export function watchScan(
  handle: ScanHandle,
  callbacks: {
    event: (event: ClientScanEvent) => void;
    connection: (message: string) => void;
  },
): () => void {
  const base = `/demo/scan/${encodeURIComponent(handle.jobId)}`;
  const query = `?session_id=${encodeURIComponent(handle.sessionKey)}`;
  let source: EventSource | null = null;
  let stopped = false;
  let polling = false;
  const controller = new AbortController();
  const seen = new Set<string>();
  const stop = () => {
    stopped = true;
    source?.close();
    clearInterval(interval);
    controller.abort();
  };
  const accept = (event: ClientScanEvent) => {
    if (stopped || !event || typeof event.type !== 'string') return;
    const key = JSON.stringify(event);
    if (seen.has(key)) return;
    seen.add(key);
    if (event.type === 'done' || event.type === 'error') stop();
    callbacks.event(event);
  };
  const poll = async () => {
    if (stopped || polling) return;
    polling = true;
    try {
      const response = await fetch(base + query, { credentials: 'same-origin', signal: controller.signal });
      if ([401, 403, 404, 410].includes(response.status)) {
        accept({ type: 'error', message: 'This scan is no longer available to this session. Reload the page to continue.' });
        return;
      }
      if (!response.ok) throw new Error('status unavailable');
      const state = (await response.json()) as { events?: ClientScanEvent[] };
      for (const event of state.events ?? []) accept(event);
      if (!stopped) callbacks.connection('');
    } catch {
      if (!stopped) callbacks.connection('Connection interrupted. Reconnecting to your existing scan…');
    } finally {
      polling = false;
    }
  };
  const interval = setInterval(() => {
    void poll();
  }, 2_000);
  void poll();
  source = new EventSource(`${base}/events${query}`);
  source.onmessage = (message) => {
    try {
      accept(JSON.parse(message.data));
    } catch {
      /* A malformed progress frame cannot replace durable status. */
    }
  };
  source.onerror = () => {
    if (!stopped) {
      callbacks.connection('Connection interrupted. Reconnecting to your existing scan…');
      void poll();
    }
    // EventSource retries the same URL itself. Polling also covers a permanently lost notification.
  };
  return stop;
}
